import React, { createContext, ReactNode, useContext, useState } from 'react';
import useMobileScreen from '../hooks/useIsMobile';
import { ApiResponseType } from './ContextType';
import { axiosInstance } from '../api';
import moment from 'moment';

type ISmartContractsContext = {
  smartContractsData: any;
  getSmartContracts: (payload: any) => void;
  isTotalPages?: number;
  getContractsDetails: (payload: any) => void;
  loading: boolean;
  isdetailsTotalPages: any;
  details: any;
  totalSmartContracts: number;
  totalSmartContractsDetail: number;
  txCount?: number;
  smartContractCountPerMinute: number;
  getTxCount: (payload: any) => void;
  getSmartContractDataPerMinute: () => void;
}

const SmartContractsContext = createContext<ISmartContractsContext | undefined>(undefined);

export const useSmartContractsContext = () => {
  const context = useContext(SmartContractsContext);
  if (!context) {
    throw new Error("useTheme must be used within a CustomToastProvider");
  }
  return context;
};

type IContextProviderProps = {
  children: ReactNode;
};

export const SmartContractsContextProvider: React.FC<IContextProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [smartContractsData, setSmartContractsData] = useState<any>();
  const [isTotalPages, setIsTotalPages] = useState<number>();
  const [isdetailsTotalPages, setDetailsIsTotalPages] = useState<number>();
  const [details, setDetails] = useState<any>();
  const [totalSmartContracts, setTotalSmartContracts] = useState<number>(0);
  const [totalSmartContractsDetail, setTotalSmartContractsDetail] = useState<number>(0);
  const [txCount, setTxCount] = useState<number>();
  const [smartContractCountPerMinute, setSmartContractCountPerMinute] = useState<number>(0);
  const mobile = useMobileScreen();

  const getSmartContractDataPerMinute = async (): Promise<ApiResponseType<any>> => {
    try {
      setLoading(true);
      let url = `/metrics/chaincodesByLastMinute`;
      const response = await axiosInstance.get(url);
      setSmartContractCountPerMinute(response?.data?.chaincode_count);
      setLoading(false);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getSmartContracts = async (payload: any): Promise<ApiResponseType<any>> => {
    try {
      const isoStartDate = payload?.blocksFromDate ? moment(payload?.blocksFromDate).format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      const isoEndDate = payload?.blocksToDate ? moment(payload?.blocksToDate).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') : '';
      setLoading(true);

      let url = (isoStartDate && isoEndDate)
        ? `/chaincode?from=${isoStartDate}&to=${isoEndDate}&page=${payload?.PageNumber}&size=${payload?.Size}`
        : `/chaincode?page=${payload?.PageNumber}&size=${payload?.Size}`;

      if (payload?.search) {
        url += `&smartContractName=${payload?.search}`;
      }

      const response = await axiosInstance.get(url);
      if(mobile) {
        setSmartContractsData(payload?.PageNumber === 1 ? response?.data?.chaincodes : [...smartContractsData, ...response?.data?.chaincodes])
      } else {
        setSmartContractsData(response?.data?.chaincodes);
      }
      setTotalSmartContracts(response?.data?.totalChaincodes)
      setIsTotalPages(response?.data?.noOfPages);
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getContractsDetails = async (payload: any): Promise<ApiResponseType<any>> => {
    try {
      const isoStartDate = payload?.blocksFromDate ? moment(payload?.blocksFromDate).format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      const isoEndDate = payload?.blocksToDate ? moment(payload?.blocksToDate).add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
      setLoading(true);

      let url = (isoStartDate && isoEndDate)
        ? `/transactions?smartContractName=${payload?.smartContractsName}&from=${isoStartDate}&to=${isoEndDate}&page=${payload?.PageNumber}&size=${payload?.Size}`
        : `/transactions?smartContractName=${payload?.smartContractsName}&page=${payload?.PageNumber}&size=${payload?.Size}`;

      if (payload?.search) {
        url += `&searchParam=${payload?.search}`;
      }

      const response = await axiosInstance.get(url);
      if(mobile) {
        setDetails(payload?.PageNumber === 1 ? response?.data?.txnsData : [...details, ...response?.data?.txnsData])
      } else {
        setDetails(response?.data?.txnsData)
      }
      setDetailsIsTotalPages(response?.data?.noOfPages);
      setTotalSmartContractsDetail(response?.data?.countOfTransactions)
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const getTxCount = async (payload: any): Promise<ApiResponseType<any>> => {
    try {
      setLoading(true);

      let url = `/transactions/count?smartContractName=${payload?.smartContractsName}`;
      const response = await axiosInstance.get(url);
      setTxCount(response?.data?.countOfTransactions)
      return {
        success: true,
        data: response?.data,
        message: response?.data?.message,
      };
    } catch (e: any) {
      setLoading(false);
      return {
        success: false,
        message: e?.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const contextValue = {
    loading,
    smartContractsData,
    getSmartContracts,
    getContractsDetails,
    isTotalPages,
    isdetailsTotalPages,
    details,
    totalSmartContracts,
    totalSmartContractsDetail,
    txCount,
    smartContractCountPerMinute,
    getTxCount,
    getSmartContractDataPerMinute,
  };

  return (
    <SmartContractsContext.Provider value={contextValue}>{children}</SmartContractsContext.Provider>
  );
};
